import { IRentedPropertyDetail } from "../types/ownerDashboard.types";

export const RENTED_PROPERTY_UNINFORMED_LABEL = "Não informado";

export interface PropertyUnitDisplay {
  apartment: string | null;
  floor: string | null;
  complement: string | null;
}

export interface PropertyListingView {
  streetAddress: string;
  unit: PropertyUnitDisplay;
  areaLabel: string;
  description: string;
}

export type PropertyUnitSource = Pick<
  IRentedPropertyDetail,
  | "apartment_number"
  | "unit"
  | "address_floor"
  | "floor"
  | "address_complement"
  | "complement"
>;

function trimValue(value: string | number | null | undefined): string {
  return String(value ?? "").trim();
}

function resolveUnitField(
  localValue: string | null | undefined,
  kenloValue: string | null | undefined
): string | null {
  const local = trimValue(localValue);
  if (local) {
    return local;
  }

  const kenlo = trimValue(kenloValue);
  return kenlo || null;
}

export function getPropertyUnitDisplay(
  source: PropertyUnitSource | null | undefined,
  fallbackComplement?: string | null
): PropertyUnitDisplay {
  if (source) {
    return {
      apartment: resolveUnitField(source.apartment_number, source.unit),
      floor: resolveUnitField(source.address_floor, source.floor),
      complement: resolveUnitField(source.address_complement, source.complement),
    };
  }

  const complement = trimValue(fallbackComplement);

  return {
    apartment: complement || null,
    floor: null,
    complement: null,
  };
}

export function buildPropertyListingView(
  detail: IRentedPropertyDetail | null | undefined,
  options?: {
    address?: string | null;
    complement?: string | null;
  }
): PropertyListingView {
  const streetAddress =
    trimValue(detail?.address) ||
    trimValue(options?.address) ||
    "Endereço não informado";

  return {
    streetAddress,
    unit: getPropertyUnitDisplay(detail, options?.complement),
    areaLabel: formatRentedPropertyArea(detail?.area),
    description: formatRentedPropertyDescription(detail?.description),
  };
}

export function hasPropertyRichDetails(view: PropertyListingView): boolean {
  return (
    view.description !== RENTED_PROPERTY_UNINFORMED_LABEL ||
    view.unit.apartment != null ||
    view.unit.floor != null ||
    view.unit.complement != null ||
    view.areaLabel !== RENTED_PROPERTY_UNINFORMED_LABEL
  );
}

function alreadyHasKeyword(value: string, keywords: string[]): boolean {
  const lower = value.toLowerCase();

  return keywords.some((keyword) => lower.includes(keyword));
}

function formatApartmentPart(value: string): string {
  if (alreadyHasKeyword(value, ["apto", "apt ", "apartamento", "unidade"])) {
    return value;
  }

  return `Apto: ${value}`;
}

function formatFloorPart(value: string): string {
  if (alreadyHasKeyword(value, ["andar", "°", "piso", "floor"])) {
    return value;
  }

  return `Andar: ${value}`;
}

function formatComplementPart(value: string): string {
  if (
    alreadyHasKeyword(value, [
      "apto",
      "apt",
      "apartamento",
      "andar",
      "°",
      "unidade",
    ])
  ) {
    return value;
  }

  return `Compl.: ${value}`;
}

export function formatAreaInlinePart(areaLabel: string): string {
  if (areaLabel === RENTED_PROPERTY_UNINFORMED_LABEL) {
    return areaLabel;
  }

  return `Metragem: ${areaLabel}`;
}

export function formatPropertyInlineLabel(listing: PropertyListingView): string {
  const parts = [
    listing.streetAddress,
    ...getPropertyInlineMetaParts(listing.unit),
    formatAreaInlinePart(listing.areaLabel),
  ].filter(Boolean);

  return parts.join(" · ");
}

function isRedundantComplement(
  complement: string,
  apartment: string | null
): boolean {
  if (!apartment) {
    return false;
  }

  const normalizedApartment = apartment.toLowerCase();
  const normalizedComplement = complement.toLowerCase();

  if (!normalizedComplement.includes(normalizedApartment)) {
    return false;
  }

  return alreadyHasKeyword(complement, ["apto", "apt", "apartamento", "unidade"]);
}

export function getPropertyInlineMetaParts(
  unit: PropertyUnitDisplay
): string[] {
  const parts: string[] = [];
  const seen = new Set<string>();

  const appendPart = (value: string | null, formatter: (part: string) => string) => {
    if (!value) {
      return;
    }

    const display = formatter(value);
    const normalized = display.toLowerCase();
    if (seen.has(normalized)) {
      return;
    }

    seen.add(normalized);
    parts.push(display);
  };

  appendPart(unit.apartment, formatApartmentPart);
  appendPart(unit.floor, formatFloorPart);

  if (unit.complement && !isRedundantComplement(unit.complement, unit.apartment)) {
    appendPart(unit.complement, formatComplementPart);
  }

  return parts;
}

export function enrichPropertyFilterLabel(
  baseLabel: string,
  detail: IRentedPropertyDetail | null | undefined
): string {
  if (!detail) {
    return baseLabel;
  }

  const view = buildPropertyListingView(detail);
  const suffixParts: string[] = [];

  getPropertyInlineMetaParts(view.unit).forEach((part) => {
    if (!baseLabel.toLowerCase().includes(part.toLowerCase())) {
      suffixParts.push(part);
    }
  });

  const areaPart = formatAreaInlinePart(view.areaLabel);

  if (
    view.areaLabel !== RENTED_PROPERTY_UNINFORMED_LABEL &&
    !baseLabel.toLowerCase().includes(areaPart.toLowerCase())
  ) {
    suffixParts.push(areaPart);
  }

  if (suffixParts.length === 0) {
    return baseLabel;
  }

  return `${baseLabel} · ${suffixParts.join(" · ")}`;
}

export function formatRentedPropertyDescription(
  description: string | null | undefined
): string {
  const normalized = trimValue(description);

  return normalized || RENTED_PROPERTY_UNINFORMED_LABEL;
}

export function formatRentedPropertyArea(
  area: number | string | null | undefined
): string {
  if (area == null || area === "") {
    return RENTED_PROPERTY_UNINFORMED_LABEL;
  }

  const normalized = trimValue(area);

  return normalized ? `${normalized} m²` : RENTED_PROPERTY_UNINFORMED_LABEL;
}

export function formatRentedPropertyUnitDetails(
  property: Pick<
    IRentedPropertyDetail,
    | "apartment_number"
    | "unit"
    | "address_floor"
    | "floor"
    | "address_complement"
    | "complement"
  >
): string | null {
  const unitParts = [
    resolveUnitField(property.apartment_number, property.unit),
    resolveUnitField(property.address_floor, property.floor),
    resolveUnitField(property.address_complement, property.complement),
  ].filter((part): part is string => Boolean(part));

  return unitParts.length > 0 ? unitParts.join(", ") : null;
}
